import React, { useCallback, useRef, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { IReturn } from "@/interfaces/Return";
import Return from "@/components/return/Return";
import ReturnSkeleton from "@/components/return/ReturnSkeleton";
import { Button } from "@/components/ui/button";
import { DEFAULT_PAGE_SIZE } from "@/constants/constants";
import {
  fetchCancellationReturnList,
  fetchReturnList,
} from "@/utils/return-list-fetching";
import {
  CANCELLATION_QUERY_PARAM,
  IS_RTO_QUERY_PARAM,
  canAccessRtoListFilter,
  usesCancellationListFilter,
} from "@/utils/return-rto";

interface ReturnListProps {
  searchText: string;
  roles: string;
  isRefreshing: boolean;
  statusId: number;
  sellerName: string;
}

const ReturnList: React.FC<ReturnListProps> = ({
  searchText,
  roles,
  isRefreshing,
  sellerName,
  statusId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnList, setReturnList] = useState<IReturn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const latestFetchRef = useRef(0);
  const canUseRtoFilter = canAccessRtoListFilter(roles);
  const canUseCancellationFilter = usesCancellationListFilter(roles);
  const isRTO =
    canUseRtoFilter && searchParams.get(IS_RTO_QUERY_PARAM) === "true";
  const isCancellation =
    canUseCancellationFilter &&
    searchParams.get(CANCELLATION_QUERY_PARAM) === "true";

  const setQueryFlag = (param: string, value: boolean) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(param, "true");
        } else {
          next.delete(param);
        }
        return next;
      },
      { replace: true }
    );
  };

  const setIsRTO = (value: boolean) => {
    if (!canUseRtoFilter) return;
    setQueryFlag(IS_RTO_QUERY_PARAM, value);
  };

  const setIsCancellation = (value: boolean) => {
    if (!canUseCancellationFilter) return;
    setQueryFlag(CANCELLATION_QUERY_PARAM, value);
  };

  const observer = useRef<IntersectionObserver>();
  const lastReturnElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPage) => prevPage + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchReturns = useCallback(
    async (page: number, rtoFilter: boolean, cancellationFilter: boolean) => {
      const requestId = ++latestFetchRef.current;

      setIsLoading(true);
      if (page === 1) {
        setReturnList([]);
      }

      try {
        const payload = {
          keyword: parseInt(searchText) || 0,
          page_number: page,
          page_size: DEFAULT_PAGE_SIZE,
          statutId: statusId,
          vendeur_libelle: sellerName,
        };

        const newReturns = cancellationFilter
          ? await fetchCancellationReturnList(payload)
          : await fetchReturnList(roles, {
              ...payload,
              ...(canUseRtoFilter && { isRTO: rtoFilter }),
            });

        if (requestId !== latestFetchRef.current) return;

        if (page === 1) {
          setReturnList(newReturns);
        } else {
          setReturnList((prev) => [...prev, ...newReturns]);
        }
        setHasMore(newReturns.length === DEFAULT_PAGE_SIZE);
      } finally {
        if (requestId === latestFetchRef.current) {
          setIsLoading(false);
        }
      }
    },
    [roles, searchText, statusId, sellerName, canUseRtoFilter]
  );

  useEffect(() => {
    setPageNumber(1);
    fetchReturns(1, isRTO, isCancellation);
  }, [
    isRefreshing,
    searchText,
    statusId,
    sellerName,
    canUseRtoFilter,
    isRTO,
    isCancellation,
    fetchReturns,
  ]);

  useEffect(() => {
    if (pageNumber > 1) {
      fetchReturns(pageNumber, isRTO, isCancellation);
    }
  }, [pageNumber, isRTO, isCancellation, fetchReturns]);

  return (
    <>
      {canUseCancellationFilter && (
        <div className="flex flex-col gap-2 mb-4 w-full">
          <div className="flex gap-2 w-full">
            <Button
              className="w-full"
              variant={!isCancellation ? "default" : "outline"}
              onClick={() => setIsCancellation(false)}
            >
              En cours de livraison
            </Button>
            <Button
              className="w-full"
              variant={isCancellation ? "default" : "outline"}
              onClick={() => setIsCancellation(true)}
            >
              En annulation
            </Button>
          </div>
        </div>
      )}

      {canUseRtoFilter && (
        <div className="flex flex-col gap-2 mb-4 w-full">
          <div className="flex gap-2 w-full">
            <Button
              className="w-full"
              variant={!isRTO ? "default" : "outline"}
              onClick={() => setIsRTO(false)}
            >
              Standard
            </Button>
            <Button
              className="w-full"
              variant={isRTO ? "default" : "outline"}
              onClick={() => setIsRTO(true)}
            >
              RTO
            </Button>
          </div>
        </div>
      )}

      {!isLoading && returnList.length === 0 && (
        <div className="text-center text-gray-500 font-sans my-2">
          Aucune tournée disponible
        </div>
      )}

      {returnList.map((item, index) => (
        <div
          key={item.numTournee}
          ref={
            index === returnList.length - 1 ? lastReturnElementRef : undefined
          }
        >
          <Return return_={item} readOnly={isCancellation} />
        </div>
      ))}
      {isLoading &&
        Array.from({ length: 3 }).map((_, index) => (
          <ReturnSkeleton key={`loading-${index}`} />
        ))}
    </>
  );
};

export default ReturnList;
